"use client";

import { useState, useEffect, useCallback } from 'react';
import { NamespaceService } from '../services/namespace-service';
import type { NamespaceResponse } from '../sdk/models';

interface UseNamespacesResult {
  namespaces: NamespaceResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createNamespace: (name: string, properties?: Record<string, unknown>) => Promise<NamespaceResponse>;
  updateNamespace: (id: string, name?: string, properties?: Record<string, unknown>) => Promise<NamespaceResponse>;
  deleteNamespace: (id: string) => Promise<void>;
  getNamespaceOptions: () => Array<{ value: string; label: string; tableCount?: number }>;
}

/**
 * Hook for managing namespaces
 * @param orgId - Organization ID
 * @returns Namespace management state and operations
 */
export function useNamespaces(orgId: string): UseNamespacesResult {
  const [namespaces, setNamespaces] = useState<NamespaceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNamespaces = useCallback(async () => {
    if (!orgId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await NamespaceService.getNamespaces(orgId);
      setNamespaces(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch namespaces');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const refetch = useCallback(async () => {
    await fetchNamespaces();
  }, [fetchNamespaces]);

  const createNamespace = useCallback(async (
    name: string, 
    properties?: Record<string, unknown>
  ): Promise<NamespaceResponse> => {
    try {
      setError(null);
      const newNamespace = await NamespaceService.createNamespace(orgId, name, properties);
      setNamespaces(prev => [...prev, newNamespace]);
      return newNamespace;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create namespace';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [orgId]);

  const updateNamespace = useCallback(async (
    id: string,
    name?: string,
    properties?: Record<string, unknown>
  ): Promise<NamespaceResponse> => {
    try {
      setError(null);
      const updatedNamespace = await NamespaceService.updateNamespace(id, orgId, name, properties);
      setNamespaces(prev => prev.map(ns => ns.id === id ? updatedNamespace : ns));
      return updatedNamespace;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update namespace';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [orgId]);

  const deleteNamespace = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      await NamespaceService.deleteNamespace(id, orgId);
      setNamespaces(prev => prev.filter(ns => ns.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete namespace';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [orgId]);

  const getNamespaceOptions = useCallback(() => {
    return namespaces.map(namespace => ({
      value: namespace.name,
      label: namespace.name,
      tableCount: namespace.table_count || 0,
    }));
  }, [namespaces]);

  useEffect(() => {
    fetchNamespaces();
  }, [fetchNamespaces]);

  return {
    namespaces,
    loading,
    error,
    refetch,
    createNamespace,
    updateNamespace,
    deleteNamespace,
    getNamespaceOptions,
  };
}